import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@material-ui/core';
import { Edit as EditIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { toggleOperationHoursViewMode } from '../../redux/actions/newOperationHoursActions';
import { clearEntity } from '../../redux/actions/restaurantsActions';
import WeekSelector from '../WeekSelector';
import RamAssistant from '../RamAssistant';
import RestaurantSelector from './RestaurantSelector';
import RestaurantInfoCard from '../OperationHoursMaster/RestaurantInfoCard';
import { useStyles } from './styles';
import OperationHoursViewTab from '../OperationHoursViewTab';
import OperationRestaurantCard from '../OperationHoursTab/RestaurantDetails';
import OperationHoursTab from '../OperationHoursTab';

function OperationHoursMassUpdateMaster() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch: any = useDispatch();
  const [selectedWeek, setSelectedWeek] = useState<Date>(new Date());
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const { shouldSwitchToViewMode } = useSelector(
    (state: RootStateOrAny) => state.NewOperationHoursReducer,
  );

  const { entity: restaurant, loading: isRestaurantLoading } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer,
  );

  // Memoized dispatch functions
  const toggleViewMode = useCallback(
    (mode: boolean) => {
      return dispatch(toggleOperationHoursViewMode(mode));
    },
    [dispatch],
  );

  // Handle switching to view mode when triggered from BaseAdjustment
  useEffect(() => {
    if (shouldSwitchToViewMode) {
      setIsEditMode(false);
      // Reset the flag
      toggleViewMode(false);
    }
  }, [shouldSwitchToViewMode, toggleViewMode]);

  const handleWeekChange = (week: Date) => {
    setSelectedWeek(week);
  };

  useEffect(() => {
    dispatch(clearEntity());
  }, [dispatch]);

  const handleEditMultiple = () => {
    // Clear restaurant selection when entering edit mode for mass updates
    dispatch(clearEntity());
    setIsEditMode(!isEditMode);
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerContainer}>
        {!isEditMode && (
          <div className={classes.leftSection}>
            <RestaurantSelector className={classes.restaurantDropdown} />
          </div>
        )}

        {!isEditMode && <RestaurantInfoCard restaurant={restaurant} />}

        <div className={classes.controlsContainer}>
          {!isEditMode && (
            <>
              <WeekSelector
                selectedWeek={selectedWeek}
                onWeekChange={handleWeekChange}
              />
              <Button
                variant="contained"
                className={classes.editButton}
                startIcon={<EditIcon />}
                onClick={handleEditMultiple}
                disabled={isRestaurantLoading || !restaurant?.RestaurantId}
              >
                {t('operationHours.editMultiple')}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className={classes.contentContainer}>
        {isEditMode ? (
          <div className={classes.editModeContainer}>
            <div className={classes.editModeHeader}>
              <div className={classes.editModeCardContainer}>
                <OperationRestaurantCard />
              </div>
              <div className={classes.editModeButtonContainer}>
                <Button
                  variant="contained"
                  className={classes.editButton}
                  startIcon={<EditIcon />}
                  onClick={handleCancel}
                >
                  {t('app.common.cancel')}
                </Button>
              </div>
            </div>
            <OperationHoursTab />
          </div>
        ) : (
          <OperationHoursViewTab selectedWeek={selectedWeek} />
        )}
      </div>

      {createPortal(<RamAssistant />, document.body)}
    </div>
  );
}

export default OperationHoursMassUpdateMaster;
